// src/components/Batch/KilnLogsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import KilnLogModal from "../Modals/KilnLogModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFeedback } from "../../contexts/FeedbackContext";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStorage } from "../../contexts/StorageContext";
import { useWorkshops } from "../../contexts/WorkshopContext";
import { useStudioSpaces } from "@/contexts/StudioSpaceContext";
import { useNotifications } from "../../contexts/NotificationsContext";
import WidgetShell from "../../ui/WidgetShell.jsx";
import Button from "../../ui/Button.jsx";
import { SmallText } from "../../ui/Typography.jsx";
import PageLayout from "../../ui/PageLayout.jsx";
import PageHeader from "../../ui/PageHeader.jsx";
import { mapToKilnSchedule } from "../../worlds/ceramics/logic/kilnAdapter.js";
import { evaluateKilnSchedule } from "../../worlds/ceramics/logic/kilnScheduleEngine.js";
import { buildKilnSuggestions } from "../../worlds/ceramics/logic/kilnSuggestionEngine.js";
import { STUDIO_CAPACITY } from "../../worlds/ceramics/logic/capacityConfig.js";
import {
  clayFireTypeLabel,
  clayFireTypeTagClass,
  clayFilterOptions,
  inferClayFireType,
  matchesClayFilter,
} from "../Kiln/kilnFireTypes";

const KILN_LS_KEY = "mudoKilnLogs";
const KILN_FILE = "kiln/kilnLogs.json";
const BATCHES_LS_KEY = "mudoBatches";
const BATCHES_FILE = "batches/mudoBatches.json";
const SETTINGS_FILE = "settings/mudoSettings.json";

const parseDateValue = (v) => {
  const t = Date.parse(v || "");
  return Number.isFinite(t) ? new Date(t) : null;
};
const displayDateValue = (v) => {
  const d = parseDateValue(v);
  return d ? d.toLocaleDateString() : "-";
};

export default function KilnLogsPage() {
  const [logs, setLogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [clayFilter, setClayFilter] = useState("all");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkFiringId = searchParams.get("firingId") || searchParams.get("kilnRunId");
  const deepLinkHandledRef = useRef(false);
  const exportRef = useRef();
  const { play, buzz } = useFeedback();
  const { addNotification, notifyAndInbox } = useNotifications() || {};

  const { hasFolder, loadJSON, saveJSON, workspaceName } = useStorage();
  const { events = [] } = usePlanner();
  const { workshops = [], bookings: workshopBookings = [] } = useWorkshops?.() || {};
  const { spaces = [], bookings: studioBookings = [] } = useStudioSpaces();
  const kilnEvents = useMemo(() => mapToKilnSchedule(logs || []), [logs]);
  const kilnSchedule = useMemo(
    () => evaluateKilnSchedule({ kilnEvents, capacity: STUDIO_CAPACITY }),
    [kilnEvents]
  );
  const kilnSuggestions = useMemo(
    () => buildKilnSuggestions({ kilnSchedule, capacity: STUDIO_CAPACITY }),
    [kilnSchedule]
  );
  useEffect(() => {
    if (!addNotification) return;
    (kilnSchedule?.conflicts || [])
      .filter((c) => c.reason === "over_capacity")
      .forEach((c) => {
        addNotification({
          id: `kiln-conflict-${c.date}-${c.kilnId}`,
          severity: "error",
          source: "kiln",
          title: "Kiln over capacity",
          body: `Kiln ${c.kilnId} is overloaded on ${c.date}. Consider rescheduling a firing.`,
          createdAt: new Date().toISOString(),
          readAt: null,
        });
      });
  }, [kilnSchedule, addNotification]);

  useEffect(() => {
    if (!deepLinkFiringId || deepLinkHandledRef.current) return;
    const target = (logs || []).find((log) => String(log.id) === String(deepLinkFiringId));
    if (target) {
      setEditingLog(target);
      setShowModal(true);
    } else if ((logs || []).length > 0) {
      notifyAndInbox?.({
        notification: {
          id: `kiln-firing-missing-${deepLinkFiringId}`,
          title: "Firing not found",
          message: "We couldn't find that kiln firing.",
          level: "warning",
          source: "kiln",
          world: "studio",
          entity: { type: "kiln_firing", id: deepLinkFiringId },
          action: {
            kind: "NAVIGATE",
            to: "/dashboard/kiln-logs",
            params: { firingId: deepLinkFiringId },
          },
        },
      });
    }
    deepLinkHandledRef.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("firingId");
      url.searchParams.delete("kilnRunId");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }, [deepLinkFiringId, logs, notifyAndInbox, searchParams]);

  // ---------- Seed logs from folder on workspace change (else LS) ----------
  useEffect(() => {
    let alive = true;

    (async () => {
      if (hasFolder) {
        const diskLogs = await loadJSON(KILN_FILE, null);
        if (!alive) return;

        if (Array.isArray(diskLogs)) {
          try {
            localStorage.setItem(KILN_LS_KEY, JSON.stringify(diskLogs));
          } catch {}
          setLogs(diskLogs);
        } else {
          try {
            localStorage.removeItem(KILN_LS_KEY);
          } catch {}
          setLogs([]);
        }
      } else {
        try {
          setLogs(JSON.parse(localStorage.getItem(KILN_LS_KEY) || "[]"));
        } catch {
          setLogs([]);
        }
      }

      // Settings (logo)
      try {
        const s = await loadJSON(SETTINGS_FILE, null);
        if (s && typeof s === "object" && s.logoUrl) setLogoUrl(s.logoUrl);
        else setLogoUrl(null);
      } catch {
        setLogoUrl(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [hasFolder, workspaceName, loadJSON]);

  // Refresh on Receive (both file-level + vault snapshot)
  useEffect(() => {
    const onRestored = async () => {
      try {
        const disk = await loadJSON(KILN_FILE, []);
        setLogs(Array.isArray(disk) ? disk : []);
      } catch {}
      try {
        const s = await loadJSON(SETTINGS_FILE, null);
        setLogoUrl(s?.logoUrl || null);
      } catch {}
    };
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:vault-restored", onRestored);
    return () => {
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:vault-restored", onRestored);
    };
  }, [loadJSON]);

  // ---------- Batch lookup (folder first, fallback LS) ----------
  const [batchesState, setBatchesState] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      let list = [];
      if (hasFolder) {
        list = await loadJSON(BATCHES_FILE, null);
      }
      if (!Array.isArray(list) || list.length === 0) {
        try {
          list = JSON.parse(localStorage.getItem(BATCHES_LS_KEY) || "[]");
        } catch {
          list = [];
        }
      }
      if (!alive) return;
      if (Array.isArray(list)) {
        try {
          localStorage.setItem(BATCHES_LS_KEY, JSON.stringify(list));
        } catch {}
        setBatchesState(list);
      } else {
        setBatchesState([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasFolder, workspaceName, loadJSON]);

  const batchMap = useMemo(() => {
    const m = new Map();
    (batchesState || []).forEach((b) => m.set(String(b.id), b.name || `Batch ${b.id}`));
    return m;
  }, [batchesState]);

  const batchOptions = useMemo(
    () => (batchesState || []).map((b) => ({ id: b.id, name: b.name || `Batch ${b.id}` })),
    [batchesState]
  );

  const batchNameOf = (id) => {
    if (id == null || id === "") return "-";
    return batchMap.get(String(id)) || "-";
  };

  // ---------- Helpers ----------
  async function mirrorKilnToFolder(next) {
    if (!hasFolder) return;
    try {
      await saveJSON(KILN_FILE, next);
    } catch {}
  }

  function handleDelete(id) {
    play("tap");
    if (!window.confirm("Delete this firing log?")) {
      buzz("light");
      return;
    }

    const next = (logs || []).filter((l) => String(l.id) !== String(id));
    try {
      localStorage.setItem(KILN_LS_KEY, JSON.stringify(next));
    } catch {}
    setLogs(next);
    mirrorKilnToFolder(next);
    play("stop");
    buzz("medium");
  }

  function applyKilnSuggestion(s) {
    if (!s) return;
    if (s.type === "reschedule_firing" && s.firingId && s.toDate) {
      const target = logs.find((l) => String(l.id) === String(s.firingId));
      if (target) {
        setEditingLog({ ...target, date: new Date(s.toDate).toISOString().slice(0, 10) });
        setShowModal(true);
        play("tap");
        buzz("light");
        return;
      }
    }
    // For merge/other suggestions, surface via modal later
    play("tap");
    buzz("light");
  }

  // ---- Infer firing type from planner events if missing/Other
  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const inferType = (log) => {
    const t = (log?.type || "").trim().toLowerCase();
    if (t && t !== "other") return capitalize(t);

    const bid = log?.batchId != null ? String(log.batchId) : null;
    if (!bid) return "Other";

    const byBatch = events.filter((e) => String(e.batchId) === bid);
    if (!byBatch.length) return "Other";

    const logTime = log?.date ? new Date(log.date).getTime() : Date.now();
    let best = null;
    let bestDist = Infinity;
    byBatch.forEach((e) => {
      const tt = new Date(e.start || e.end || Date.now()).getTime();
      const dist = Math.abs(tt - logTime);
      if (dist < bestDist) {
        bestDist = dist;
        best = e;
      }
    });

    const txt = `${best?.title || ""} ${best?.taskType || ""}`.toLowerCase();
    if (txt.includes("glaze") && txt.includes("firing")) return "Glaze";
    if (txt.includes("bisque")) return "Bisque";
    if (txt.includes("glaze")) return "Glaze";
    return "Other";
  };

  const logsWithClay = useMemo(
    () => (logs || []).map((l) => ({ ...l, clayFireType: inferClayFireType(l) })),
    [logs]
  );
  const filteredLogs = useMemo(
    () =>
      clayFilter === "all"
        ? logsWithClay
        : logsWithClay.filter((log) => matchesClayFilter(log, clayFilter)),
    [logsWithClay, clayFilter]
  );

  const kilnSpacesMap = useMemo(() => {
    const m = new Map();
    (spaces || []).forEach((s) => {
      const tags = (s.tags || []).map((t) => t.toLowerCase());
      const isKiln =
        s?.type === "kiln" ||
        tags.includes("kiln") ||
        tags.includes("kilns") ||
        s?.resources?.kilns ||
        tags.some((t) => t.includes("kiln"));
      if (isKiln) m.set(s.id, s);
    });
    return m;
  }, [spaces]);

  const linkedBatches = useMemo(
    () =>
      (batchesState || []).map((b) => ({
        id: `batch-${b.id}`,
        sourceType: "batch",
        sourceName: b.name || `Batch ${b.id}`,
        date: b.targetDate || b.deadline || b.date || b.updatedAt || b.createdAt,
        estimatedPieces: Array.isArray(b?.products) ? b.products.length : undefined,
        clayFireType: inferClayFireType(b),
      })),
    [batchesState]
  );

  const linkedStudioBookings = useMemo(() => {
    if (!studioBookings?.length) return [];
    return studioBookings
      .filter((b) => kilnSpacesMap.has(b.spaceId))
      .map((b) => {
        const space = kilnSpacesMap.get(b.spaceId);
        return {
          id: `booking-${b.id || b.date || Math.random().toString(36).slice(2, 8)}`,
          sourceType: "booking",
          sourceName: space?.name || "Kiln booking",
          date: b.date || b.startTime || b.endTime,
          estimatedPieces: Number.isFinite(Number(b.seats)) ? Number(b.seats) : undefined,
          clayFireType: inferClayFireType({ ...space, ...b }),
          timeRange:
            b.startTime && b.endTime ? `${String(b.startTime).slice(0, 5)}–${String(b.endTime).slice(0, 5)}` : null,
        };
      })
      .sort((a, b) => {
        const ta = parseDateValue(a.date)?.getTime() ?? 0;
        const tb = parseDateValue(b.date)?.getTime() ?? 0;
        return ta - tb;
      });
  }, [studioBookings, kilnSpacesMap]);

  const workshopBookingSummary = useMemo(() => {
    const m = new Map();
    (workshopBookings || []).forEach((b) => {
      const seats = Number(b.seats) || 0;
      const piecesPerSeat = Number(b.piecesPerSeat) || 1;
      const prev = m.get(b.workshopId) || { seats: 0, pieces: 0 };
      m.set(b.workshopId, {
        seats: prev.seats + seats,
        pieces: prev.pieces + seats * piecesPerSeat,
      });
    });
    return m;
  }, [workshopBookings]);

  const linkedWorkshops = useMemo(
    () =>
      (workshops || []).map((w) => {
        const summary = workshopBookingSummary.get(w.id) || { seats: 0, pieces: 0 };
        return {
          id: `workshop-${w.id}`,
          sourceType: "workshop",
          sourceName: w.title || w.name || "Workshop",
          date: w.date || w.startDate || w.when || w.scheduledAt || w.createdAt,
          estimatedPieces: summary.pieces || summary.seats || undefined,
          clayFireType: inferClayFireType(w),
        };
      }),
    [workshops, workshopBookingSummary]
  );

  const filteredBatches = useMemo(
    () =>
      clayFilter === "all"
        ? linkedBatches
        : linkedBatches.filter((item) => matchesClayFilter(item, clayFilter)),
    [linkedBatches, clayFilter]
  );
  const filteredStudioBookings = useMemo(
    () =>
      clayFilter === "all"
        ? linkedStudioBookings
        : linkedStudioBookings.filter((item) => matchesClayFilter(item, clayFilter)),
    [linkedStudioBookings, clayFilter]
  );
  const filteredWorkshops = useMemo(
    () =>
      clayFilter === "all"
        ? linkedWorkshops
        : linkedWorkshops.filter((item) => matchesClayFilter(item, clayFilter)),
    [linkedWorkshops, clayFilter]
  );

  // ---- Exports
  const exportPDF = async () => {
    if (!exportRef.current) return;
    const canvas = await html2canvas(exportRef.current);
    const pdf = new jsPDF("p", "mm", "a4");
    const imgData = canvas.toDataURL("image/png");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = (canvas.height * pageWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
    pdf.save("kiln-logs.pdf");
    play("success");
    buzz("light");
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [
      ["Date", "Type", "Temp (°C)", "Duration (hrs)", "Status", "Batch", "Notes"],
      ...logs.map((log) => [
        log.date || "",
        inferType(log),
        log.temperature ?? "",
        log.durationHours ?? "",
        log.status || "",
        batchNameOf(log.batchId),
        log.notes || "",
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Kiln Logs");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "kiln-logs.xlsx");
    play("success");
    buzz("light");
  };

  const exportCSV = () => {
    const header = ["Date", "Type", "Temp (°C)", "Duration (hrs)", "Status", "Batch", "Notes"];
    const rows = logs.map((log) => [
      log.date || "",
      inferType(log),
      log.temperature ?? "",
      log.durationHours ?? "",
      log.status || "",
      batchNameOf(log.batchId),
      (log.notes || "").replace(/\n/g, " "),
    ]);
    const csvContent = [header, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "kiln-logs.csv");
    play("success");
    buzz("light");
  };

  const renderClayTag = (value) => (
    <span className={clayFireTypeTagClass(value || "other")}>{clayFireTypeLabel(value || "other")}</span>
  );

  const renderLinkedGroup = (title, list, emptyText) => (
    <div className="rounded-2xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]/80 backdrop-blur p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm text-[var(--mudo-text)]">{title}</div>
        <div className="text-[11px] text-[var(--mudo-text-muted)]">{list.length} items</div>
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-[var(--mudo-text-muted)]">{emptyText}</div>
      ) : (
        <ul className="space-y-2 text-sm text-[var(--mudo-text)]">
          {list.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-2 border border-[var(--mudo-border)] rounded-xl p-2 bg-[var(--mudo-bg-soft)]/70">
              <div>
                <div className="font-medium text-[var(--mudo-text)]">{item.sourceName}</div>
                <div className="text-xs text-[var(--mudo-text-muted)]">
                  {displayDateValue(item.date)}
                  {item.timeRange ? ` · ${item.timeRange}` : ""}
                </div>
                {item.estimatedPieces ? (
                  <div className="text-xs text-[var(--mudo-text)]">Estimated pieces: {item.estimatedPieces}</div>
                ) : null}
              </div>
              <div className="shrink-0">{renderClayTag(item.clayFireType)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <PageLayout
      header={<PageHeader title="Kiln" subtitle="Schedule & capacity" showBack />}
    >
      <div className="space-y-4" ref={exportRef}>
      {logoUrl && (
        <div className="mb-4 text-center">
          <img src={logoUrl} alt="Logo" className="h-16 w-auto inline-block" />
        </div>
      )}

      <WidgetShell title="Kiln schedule" className="space-y-4">

      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-semibold text-[var(--mudo-text)]">Kiln Firing Logs</h1>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="px-3 py-1 text-sm"
            onClick={() => {
              play("tap");
              buzz("light");
              navigate("/dashboard");
            }}
          >
            Dashboard
          </Button>
          <Button
            variant="primary"
            className="px-3 py-1 text-sm"
            onClick={() => {
              setEditingLog(null);
              setShowModal(true);
              play("start");
              buzz("light");
            }}
          >
            + New Firing Log
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-2 text-xs no-print">
        {clayFilterOptions.map((opt) => {
          const active = clayFilter === opt.value;
          return (
            <Button
              key={opt.value}
              variant={active ? "primary" : "soft"}
              className="px-3 py-1 text-xs"
              onClick={() => setClayFilter(opt.value)}
            >
              {opt.label}
            </Button>
          );
        })}
      </div>

      <div className="mb-2 flex flex-wrap gap-2 no-print">
        <Button variant="soft" className="px-3 py-1 text-sm" onClick={exportPDF}>
          Export PDF
        </Button>
        <Button variant="soft" className="px-3 py-1 text-sm" onClick={exportExcel}>
          Export Excel
        </Button>
        <Button variant="soft" className="px-3 py-1 text-sm" onClick={exportCSV}>
          Export CSV
        </Button>
        <Button
          variant="ghost"
          className="px-3 py-1 text-sm"
          onClick={() => {
            window.print();
            play("tap");
          }}
        >
          Print
        </Button>
      </div>

      </WidgetShell>

      <WidgetShell title="Capacity & suggestions" className="mb-4">
        {kilnSuggestions.length > 0 ? (
          <div className="space-y-2">
            {kilnSuggestions.slice(0, 3).map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-3 text-sm">
                <div>
                  <div className="font-medium text-[var(--mudo-text)]">{s.title}</div>
                  <SmallText className="text-[var(--mudo-text-muted)]">{s.message}</SmallText>
                </div>
                <Button variant="soft" onClick={() => applyKilnSuggestion(s)}>
                  Apply
                </Button>
              </div>
            ))}
            {kilnSuggestions.length > 3 && (
              <SmallText className="text-[var(--mudo-text-muted)]">
                {kilnSuggestions.length - 3} more suggestions available
              </SmallText>
            )}
          </div>
        ) : (
          <SmallText className="text-[var(--mudo-text-muted)]">No capacity warnings right now.</SmallText>
        )}
      </WidgetShell>

      {filteredLogs.length === 0 ? (
        <p className="text-[var(--mudo-text-muted)]">
          {logsWithClay.length === 0
            ? "No kiln firings logged yet."
            : "No kiln firings match this clay fire type yet."}
        </p>
      ) : (
        <table className="min-w-full text-sm bg-[var(--mudo-bg-soft)]/80 text-[var(--mudo-text)] rounded">
          <thead className="border-b border-[var(--mudo-border)]">
            <tr className="text-left">
              <th className="p-2">Date</th>
              <th className="p-2">Type</th>
              <th className="p-2">Temp (°C)</th>
              <th className="p-2">Duration (hrs)</th>
              <th className="p-2">Status</th>
              <th className="p-2">Batch</th>
              <th className="p-2">Clay</th>
              <th className="p-2 no-print">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => {
              const name = batchNameOf(log.batchId);
              const isLinked = name !== "-";
              return (
                <tr key={log.id} className="border-t border-[var(--mudo-border)]">
                  <td className="p-2">{log.date}</td>
                  <td className="p-2">{inferType(log)}</td>
                  <td className="p-2">{log.temperature}</td>
                  <td className="p-2">{log.durationHours}</td>
                  <td className="p-2">{log.status}</td>
                  <td className="p-2">
                    {isLinked ? (
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs underline"
                        title="Open batch"
                        onClick={() => navigate(`/batches/${log.batchId}`)}
                      >
                        {name}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2">{renderClayTag(log.clayFireType)}</td>
                  <td className="p-2 space-x-2 no-print">
                    <Button
                      variant="soft"
                      className="px-2 py-1 text-xs"
                      onClick={() => {
                        setEditingLog(log);
                        setShowModal(true);
                        play("tap");
                        buzz("light");
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="px-2 py-1 text-xs"
                      onClick={() => handleDelete(log.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="mt-8 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[var(--mudo-text)]">Linked Studio Activity</h2>
            <p className="text-sm text-[var(--mudo-text-muted)]">
              Read-only glimpse of batches, studio bookings, and workshops that might feed kiln loads.
            </p>
          </div>
          <span className="text-xs text-[var(--mudo-text-muted)]">
            Filter: {clayFilterOptions.find((o) => o.value === clayFilter)?.label || "All"}
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {renderLinkedGroup("Upcoming from Batches", filteredBatches, "No batches linked yet.")}
          {renderLinkedGroup(
            "Upcoming from Bookings",
            filteredStudioBookings,
            "No kiln studio bookings yet."
          )}
          {renderLinkedGroup(
            "Upcoming from Workshops",
            filteredWorkshops,
            "No workshops scheduled yet."
          )}
        </div>
      </div>

      </div>

      {showModal && (
        <KilnLogModal
          onClose={(saved) => {
            setShowModal(false);
            if (saved) {
              try {
                const next = JSON.parse(localStorage.getItem(KILN_LS_KEY) || "[]");
                setLogs(Array.isArray(next) ? next : []);
                mirrorKilnToFolder(Array.isArray(next) ? next : []); // mirror edits from modal to folder
              } catch {}
            }
          }}
          initialData={editingLog}
          batchOptions={batchOptions}
        />
      )}
    </PageLayout>
  );
}
